import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "425",
  "slug": "satellite-orbits-explained-leo-geo-sun-synchronous",
  "title": "Satellite Orbits Explained: LEO, GEO, and Sun-Synchronous",
  "excerpt": "Why do weather satellites seem to hover while imaging satellites race around the planet? A plain-language guide to the orbits that shape what we see from space.",
  "date": "2026-06-13",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1614730321146-b6fa6a46bcb4?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxzYXRlbGxpdGUlMjBvcmJpdCUyMGVhcnRofGVufDF8MHx8fDE3ODEzMzg1MTZ8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "Satellites sit at different heights for different jobs. Low Earth orbit suits Earth-imaging satellites like Landsat and Sentinel, geostationary orbit lets weather and communication satellites hover over one spot, and sun-synchronous orbits guarantee consistent lighting for clean imagery.",
  "content": [
    {
      "type": "paragraph",
      "text": "Every satellite image you have ever seen was shaped by the path its camera follows around the planet. The orbit decides how often a place is photographed, how detailed the image can be, and even what time of day the light falls across it. Understanding a handful of orbit types explains a surprising amount about modern Earth observation."
    },
    {
      "type": "heading",
      "text": "First, Why Satellites Do Not Fall"
    },
    {
      "type": "paragraph",
      "text": "A satellite is, in a sense, constantly falling toward Earth and constantly missing. It moves sideways so fast that as gravity pulls it down, the curved surface of the planet falls away beneath it at the same rate. The result is a stable loop. The higher the orbit, the slower it needs to travel to stay up, which is the key fact behind everything that follows."
    },
    {
      "type": "heading",
      "text": "Low Earth Orbit (LEO)"
    },
    {
      "type": "paragraph",
      "text": "Low Earth orbit sits roughly 160 to 2,000 kilometres above the surface. Satellites down here move fast, circling the planet in about 90 minutes, and the International Space Station orbits at around 400 kilometres. Being close to the ground is a huge advantage for cameras, because detail improves the nearer you are to your subject."
    },
    {
      "type": "paragraph",
      "text": "This is where most Earth-observation satellites live, including the Landsat series and the European Sentinel satellites. The trade-off is coverage. From low down, each pass only sees a narrow strip of the planet, so a single satellite cannot watch the whole Earth at once. It has to keep circling and rebuild the global picture strip by strip, pass after pass."
    },
    {
      "type": "heading",
      "text": "Geostationary Orbit (GEO)"
    },
    {
      "type": "paragraph",
      "text": "Climb all the way out to 35,786 kilometres above the equator and something remarkable happens. At that exact height a satellite takes precisely 24 hours to complete one orbit, matching the Earth’s rotation. From the ground it appears to hang motionless in the sky. This is geostationary orbit."
    },
    {
      "type": "paragraph",
      "text": "Hovering over one spot is perfect for two jobs. Weather satellites such as the GOES and Meteosat families stare at the same hemisphere continuously, which is how we get those looping cloud animations of a hurricane forming and spinning toward a coast. Communication and television satellites use it too, which is why a satellite dish can be bolted to a wall pointing at one fixed patch of sky and never need to move again."
    },
    {
      "type": "heading",
      "text": "Medium Earth Orbit (MEO)"
    },
    {
      "type": "paragraph",
      "text": "Between the two sits medium Earth orbit, from about 2,000 kilometres up to the geostationary belt. The most important residents here are navigation constellations. GPS satellites orbit at roughly 20,200 kilometres, high enough that a handful of them can blanket the globe with signal, but not so high that the timing signals weaken too much. Every time your phone pins your location, it is listening to this band."
    },
    {
      "type": "heading",
      "text": "Sun-Synchronous Orbit: The Photographer’s Favourite"
    },
    {
      "type": "paragraph",
      "text": "Here is the clever one. A sun-synchronous orbit is a special kind of near-polar low Earth orbit, tuned so the satellite passes over every point on Earth at the same local solar time. If it crosses your town at 10:30 in the morning today, it will cross at 10:30 in the morning on every future pass. Engineers achieve this by using the slight bulge of the Earth to nudge the orbit a tiny amount each day, exactly enough to keep pace with the planet circling the sun."
    },
    {
      "type": "paragraph",
      "text": "That consistency is gold for imaging. A similar local solar time helps comparisons, but sun elevation and shadows still change with season and latitude. Landsat and Sentinel-2 both fly sun-synchronous orbits, and it is a big part of why their imagery is so useful for tracking change over time."
    },
    {
      "type": "heading",
      "text": "Geostationary vs Geosynchronous"
    },
    {
      "type": "paragraph",
      "text": "One last point that trips people up. A geosynchronous orbit has a 24-hour period but can be tilted, so the satellite drifts north and south over a day, tracing a slow figure-eight in the sky. A geostationary orbit is the special case that sits directly over the equator with no tilt, so it appears completely fixed. All geostationary orbits are geosynchronous, but not the other way around."
    },
    {
      "type": "heading",
      "text": "Why the Orbit Shapes the Image"
    },
    {
      "type": "paragraph",
      "text": "Put it together and the logic falls out cleanly:"
    },
    {
      "type": "list",
      "items": [
        "Want sharp detail? Fly low, in LEO, and accept narrow coverage.",
        "Want to watch one place nonstop? Park high, in GEO, and accept coarse detail from far away.",
        "Want clean, comparable images over time? Use a sun-synchronous orbit for consistent lighting.",
        "Want global navigation? Spread a constellation across MEO."
      ]
    }
  ]
};
